import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { getAllRegistrations } from '../services/dashboard.service'
import { checkStatus } from '../utils/filter'
import { getApiErrorMessage } from '../utils/error'
import { EMPTY_SEARCH, normalizeSearch } from '../utils/search'

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

// Tabel mengambil data per blok 100 baris. Selama user pindah-pindah halaman
// di dalam blok yang sama tidak ada request baru sama sekali; request
// berikutnya baru jalan waktu dia keluar dari blok itu (baris 1-100 ke
// 101-200, dan seterusnya).
export const FETCH_CHUNK_SIZE = 100

const toastError = (error) => {
  const message = getApiErrorMessage(error, 'Gagal memuat data pendaftar')
  toast.error(message, { toastId: message })
}

// Filter yang sama dengan yang dipakai backend, cuma dipakai sebagai jaring
// pengaman kalau backend ternyata belum mengembalikan meta pagination (lihat
// normalize di bawah). Semua kriteria di-AND, sama seperti di server.
const includes = (value, term) =>
  String(value ?? '')
    .toLowerCase()
    .includes(term.toLowerCase())

export const filterRegistrations = (rows, { status, korwil, search }) => {
  const { fullname, nik, email } = normalizeSearch(search)
  return rows
    .filter((row) => (status === 'All' ? true : checkStatus(row, status)))
    .filter((row) =>
      korwil === '' ? true : row.korwil?.id?.toString() === korwil,
    )
    .filter((row) => (fullname ? includes(row.fullname, fullname) : true))
    .filter((row) =>
      nik ? includes(String(row.nik ?? '').replace(/\s+/g, ''), nik) : true,
    )
    .filter((row) => (email ? includes(row.email, email) : true))
}

// Server meng-clamp pageSize di 100, jadi minta lebih dari itu percuma.
const BULK_PAGE_SIZE = 100

// Ambil seluruh data yang cocok dengan filter dengan cara meminta halaman demi
// halaman, bukan satu request besar. Kalau backend belum mendukung pagination
// (tidak ada meta), hasilnya difilter di sisi client.
//
// `onProgress` dipanggil setiap satu halaman selesai dengan { loaded, total }
// supaya UI bisa menampilkan progress. `signal` dari AbortController dipakai
// untuk membatalkan di tengah jalan: request yang sedang jalan ikut dibatalkan
// dan loop-nya berhenti karena getAllRegistrations melempar CanceledError.
export const fetchAllRegistrations = async ({
  status = 'All',
  korwil = '',
  search = EMPTY_SEARCH,
  onProgress,
  signal,
} = {}) => {
  const report = (loaded, total) => onProgress?.({ loaded, total })

  const first = await getAllRegistrations({
    status,
    korwil,
    search,
    page: 1,
    pageSize: BULK_PAGE_SIZE,
    signal,
  })
  const rows = first?.data ?? []
  const meta = first?.pagination ?? first?.meta?.pagination ?? null

  if (!meta) {
    const filtered = filterRegistrations(rows, { status, korwil, search })
    report(filtered.length, filtered.length)
    return filtered
  }

  const total = meta.total ?? rows.length
  const pageCount = meta.pageCount ?? Math.ceil(total / BULK_PAGE_SIZE)
  report(rows.length, total)

  for (let page = 2; page <= pageCount; page++) {
    const next = await getAllRegistrations({
      status,
      korwil,
      search,
      page,
      pageSize: BULK_PAGE_SIZE,
      signal,
    })
    rows.push(...(next?.data ?? []))
    report(rows.length, total)
  }
  return rows
}


/**
 * Backend mengembalikan `{ data, pagination: { page, pageSize, pageCount, total } }`
 * kalau pagination didukung (sama seperti /api/active-members). Kalau meta itu
 * tidak ada, artinya endpoint masih mengembalikan seluruh data, jadi kita
 * potong sendiri di sisi client supaya tampilan tetap benar.
 */
const normalize = (
  response,
  { status, korwil, search, page, pageSize, chunk, chunkSize },
) => {
  const rows = response?.data ?? []
  const meta = response?.pagination ?? response?.meta?.pagination ?? null
  const offset = (page - 1) * pageSize

  if (meta) {
    const total = meta.total ?? rows.length
    // `rows` isinya satu blok (chunkSize baris) yang dimulai di baris
    // (chunk - 1) * chunkSize, jadi dipotong lagi ke halaman yang dibuka.
    // `meta.pageCount` dihitung backend pakai chunkSize, bukan pageSize, jadi
    // jumlah halaman tabel kita hitung sendiri.
    const start = offset - (chunk - 1) * chunkSize
    return {
      rows: rows.slice(start, start + pageSize),
      total,
      pageCount: Math.ceil(total / pageSize),
      isServerPaginated: true,
    }
  }

  const filtered = filterRegistrations(rows, { status, korwil, search })
  return {
    rows: filtered.slice(offset, offset + pageSize),
    total: filtered.length,
    pageCount: Math.ceil(filtered.length / pageSize),
    isServerPaginated: false,
  }
}

export const useRegistrations = ({
  status = 'All',
  korwil = '',
  search = EMPTY_SEARCH,
  page = 1,
  pageSize = 10,
}) => {
  const [response, setResponse] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const warnedRef = useRef(false)
  // Perubahan status disimpan lokal per baris. Tanpa ini satu-satunya cara
  // menampilkan hasil approve adalah memuat ulang halaman, yang membuang
  // filter korwil, tab, pencarian, dan posisi halaman yang sedang dibuka.
  const [patches, setPatches] = useState({})

  // Dipecah jadi primitif: objek `search` identitasnya baru tiap render, jadi
  // kalau dipakai langsung sebagai dependensi, request jalan terus-menerus.
  const { fullname = '', nik = '', email = '' } = search ?? {}

  // Blok 100 baris yang memuat halaman yang sedang dibuka. Semua pilihan di
  // PAGE_SIZE_OPTIONS habis membagi 100, jadi satu halaman tidak pernah
  // kepotong di antara dua blok.
  const chunkSize = Math.max(FETCH_CHUNK_SIZE, pageSize)
  const chunk = Math.floor(((page - 1) * pageSize) / chunkSize) + 1

  useEffect(() => {
    let ignore = false
    const fetch = async () => {
      try {
        setIsLoading(true)
        const data = await getAllRegistrations({
          status,
          korwil,
          search: { fullname, nik, email },
          page: chunk,
          pageSize: chunkSize,
        })
        if (!ignore) {
          // Data server yang baru lebih benar daripada tambalan lokal.
          setResponse(data)
          setPatches({})
        }
      } catch (error) {
        if (!ignore) {
          setResponse(null)
          toastError(error)
        }
      } finally {
        if (!ignore) {
          setIsLoading(false)
        }
      }
    }
    fetch()
    return () => {
      ignore = true
    }
  }, [status, korwil, fullname, nik, email, chunk, chunkSize])

  const result = useMemo(
    () =>
      normalize(response, {
        status,
        korwil,
        search: { fullname, nik, email },
        page,
        pageSize,
        chunk,
        chunkSize,
      }),
    [
      response,
      status,
      korwil,
      fullname,
      nik,
      email,
      page,
      pageSize,
      chunk,
      chunkSize,
    ],
  )

  const rows = useMemo(
    () =>
      result.rows.map((row) =>
        patches[row.id] ? { ...row, ...patches[row.id] } : row,
      ),
    [result.rows, patches],
  )

  const patchRow = useCallback((id, patch) => {
    setPatches((current) => ({ ...current, [id]: { ...current[id], ...patch } }))
  }, [])

  if (response && !result.isServerPaginated && !warnedRef.current) {
    warnedRef.current = true
    console.warn(
      '[registrations] Endpoint belum mengembalikan meta pagination, data dipotong di sisi client.',
    )
  }

  return { ...result, rows, patchRow, isLoading }
}

const COUNT_STATUSES = [
  ['belumVerifikasi', 'Belum verifikasi'],
  ['verifKorwil', 'Verif oleh Korwil'],
  ['disetujui', 'Disetujui'],
]

const EMPTY_COUNTS = {
  total: 0,
  belumVerifikasi: 0,
  verifKorwil: 0,
  disetujui: 0,
}

const totalOf = (response) =>
  response?.pagination?.total ?? response?.meta?.pagination?.total ?? null

// Kartu ringkasan cuma butuh angka, bukan barisnya. Jadi kita minta 1 baris
// per status dan baca `pagination.total`-nya -- jauh lebih murah daripada
// menarik seluruh data pendaftar cuma untuk dihitung di browser.
export const useRegistrationCounts = () => {
  const [counts, setCounts] = useState(EMPTY_COUNTS)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let ignore = false
    const fetch = async () => {
      try {
        setIsLoading(true)
        const first = await getAllRegistrations({ page: 1, pageSize: 1 })
        const rows = first?.data ?? []
        const total = totalOf(first)

        // Dua tanda backend belum benar-benar paginated: tidak ada meta
        // pagination, atau kita minta 1 baris tapi yang balik lebih dari 1
        // (berarti pageSize diabaikan). Di dua kasus itu response ini sudah
        // berisi semua data, jadi dihitung di sini saja -- percuma menembak 3
        // request lagi yang sama beratnya.
        if (total === null || rows.length > 1) {
          if (!ignore) {
            setCounts(
              COUNT_STATUSES.reduce(
                (result, [key, status]) => ({
                  ...result,
                  [key]: rows.filter((row) => checkStatus(row, status)).length,
                }),
                { ...EMPTY_COUNTS, total: total ?? rows.length },
              ),
            )
          }
          return
        }

        const responses = await Promise.all(
          COUNT_STATUSES.map(([, status]) =>
            getAllRegistrations({ page: 1, pageSize: 1, status }),
          ),
        )
        if (!ignore) {
          setCounts(
            COUNT_STATUSES.reduce(
              (result, [key], index) => ({
                ...result,
                [key]: totalOf(responses[index]) ?? 0,
              }),
              { ...EMPTY_COUNTS, total },
            ),
          )
        }
      } catch (error) {
        if (!ignore) {
          setCounts(EMPTY_COUNTS)
          toastError(error)
        }
      } finally {
        if (!ignore) {
          setIsLoading(false)
        }
      }
    }
    fetch()
    return () => {
      ignore = true
    }
  }, [])

  return { counts, isLoading }
}
